import React, {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  ClipboardEvent,
  useRef,
} from "react";

interface GlobalTextInputFieldProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  title?: string;
  type?: string;
  fontSize?: string;
  fontWeight?: string;
  leadingElement?: React.ReactNode;
  trailingElement?: React.ReactNode;
  trailingElementOnClick?: () => void;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  readonly?: boolean;
  borderColor?: string;
  is2FA?: boolean; // Prop to indicate if the input is for 2FA
  showSearchResults?: boolean;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  prefixIcon?: React.ReactNode;
  prefixIconPadding?: string;
  suffixIcon?: React.ReactNode; // New prop for the suffix icon
  suffixIconOnClick?: () => void; // Click handler for the suffix icon
  allowDecimal?: boolean;
  full?: boolean;
  telegram?: boolean;
  inputBorder?: string;
  disabled?: boolean;
}

const GlobalTextInputField: React.FC<GlobalTextInputFieldProps> = ({
  value,
  onChange,
  placeholder = "",
  className = "",
  title = "",
  type = "text",
  label,
  leadingElement,
  trailingElement,
  borderColor,
  fontSize = "14px",
  fontWeight = "font-normal",
  onKeyDown,
  trailingElementOnClick,
  onSubmit,
  readonly = false,
  is2FA = false,
  showSearchResults = false,
  onClick,
  prefixIcon,
  prefixIconPadding = "pl-[40px]",
  suffixIcon,
  suffixIconOnClick,
  allowDecimal = true,
  full = false,
  telegram = false,
  inputBorder,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle keydown event for 2FA input (allow only numbers and limit to 6 digits)
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const isAllowedKey = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "v",
    ].includes(key);

    if (type === "number") {
      // Prevent up/down arrow keys from incrementing/decrementing the value
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "e" ||
        key === "+" ||
        key === "-"
      ) {
        e.preventDefault();
      }
      // Prevent decimal points if allowDecimal is false
      if (!allowDecimal && key === ".") {
        e.preventDefault();
      }
    }

    if (is2FA) {
      const inputValue = (e.target as HTMLInputElement).value;
      if (inputValue.length >= 6 && !isAllowedKey) {
        e.preventDefault();
      }
      if (!key.match(/[0-9]/) && !isAllowedKey) {
        e.preventDefault();
      }
    }

    if (key === "Enter") {
      e.preventDefault();
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    if (type === "number") {
      if (allowDecimal) {
        // Allow only digits and a single decimal point
        const regex = /^\d*(\.\d*)?$/;
        if (!regex.test(inputValue)) {
          inputValue = value; // Revert to the last valid value
        }
      } else {
        // If decimals are not allowed, strip out all non-numeric characters including '.'
        inputValue = inputValue.replace(/[^0-9]/g, "");

        // Directly update the input element to prevent flickering
        if (inputRef.current) {
          inputRef.current.value = inputValue;
        }
      }
    }

    // Pass the sanitized value to the onChange handler
    onChange({
      ...e,
      target: {
        ...e.target,
        value: inputValue,
      },
    });
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (type === "number" || is2FA) {
      e.preventDefault();
      const pasteData = e.clipboardData.getData("Text");
      let filteredData = pasteData;

      if (type === "number") {
        // Filter to allow only numbers and decimal point if allowed
        filteredData = allowDecimal
          ? pasteData.replace(/[^0-9.]/g, "")
          : pasteData.replace(/[^0-9]/g, "");

        // Ensure only one decimal point
        if (allowDecimal) {
          const parts = filteredData.split(".");
          if (parts.length > 2) {
            filteredData = parts[0] + "." + parts.slice(1).join("");
          }
        }
      } else if (is2FA) {
        filteredData = pasteData.replace(/[^0-9]/g, "").slice(0, 6);
      }

      if (inputRef.current) {
        const input = inputRef.current;
        const currentValue = input.value;
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;
        const newValue =
          currentValue.substring(0, selectionStart) +
          filteredData +
          currentValue.substring(selectionEnd);

        const finalValue = is2FA ? newValue.slice(0, 6) : newValue;
        input.value = finalValue;
        onChange({
          target: {
            value: finalValue,
          },
        } as ChangeEvent<HTMLInputElement>);
      }
    }
  };

  // Determine border classes based on showSearchResults
  const borderClasses = showSearchResults
    ? "border-t-bg-button-primary border-l-bg-button-primary border-r-bg-button-primary border-b-border-subtle"
    : inputBorder
    ? inputBorder
    : "border-border-subtle rounded-b-lg";
  // border-border-subtle rounded-b-lg
  return (
    <div className={`flex flex-col ${className}`}>
      {title && (
        <>
          <span className="text-text-base font-normal text-[14px]">
            {title}
          </span>
          <div className="my-[3px]" />
        </>
      )}
      <form onSubmit={onSubmit}>
        {label && (
          <label htmlFor="" className="text-text-base text-[14px] font-normal">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leadingElement && (
            <div className="absolute inset-y-0 left-0 flex items-center ">
              {typeof leadingElement === "string" ? (
                <img src={leadingElement} alt="" />
              ) : (
                leadingElement
              )}
            </div>
          )}
          {prefixIcon && ( // Add the prefix icon
            <div className="absolute inset-y-0 left-0 flex items-center pl-[6px] h-full">
              {prefixIcon}
            </div>
          )}
          <input
            type={type}
            placeholder={placeholder}
            className={`w-full ${
              telegram ? "pl-[72px]" : ""
            } px-2 py-[10px] text-text-base text-[${fontSize}] ${fontWeight} rounded-t-lg bg-transparent border ${borderClasses}
  ${
    inputBorder ? "" : "focus:border-b-border-subtle"
  } focus:outline-none placeholder:text-text-header ${
              leadingElement || prefixIcon ? `${prefixIconPadding}` : ""
            } ${suffixIcon ? "pr-[40px]" : ""} ${
              type === "number" ? "no-spinner" : ""
            }`} // Add the `no-spinner` class here
            value={value}
            ref={inputRef}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={handleInputChange}
            readOnly={readonly}
            onClick={onClick}
            onWheel={handleWheel}
            pattern={
              type === "number" && allowDecimal
                ? "^\\d*(\\.\\d{0,})?$"
                : undefined
            } // Add the pattern
            inputMode={
              type === "number"
                ? allowDecimal
                  ? "decimal"
                  : "numeric"
                : undefined
            } // Add the input mode
            style={{ outline: "none" }}
            {...props}
          />
          {trailingElement && (
            <span
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-text-base font-semibold text-[14px] cursor-pointer"
              onClick={trailingElementOnClick}>
              {trailingElement}
            </span>
          )}
          {suffixIcon && (
            <span
              className="absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer"
              onClick={suffixIconOnClick}>
              {suffixIcon}
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export default GlobalTextInputField;
